<template>
  <div class="interview-list-container">
    <div class="interview-list-header">
      <h2>面接一覧</h2>
      <div class="header-actions">
        <button @click="refreshList" class="btn-refresh" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i> 更新
        </button>
      </div>
    </div>

    <!-- 検索・フィルタ -->
    <div class="filter-section">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="応募者名・コメントで検索"
          @input="debounceSearch"
          class="search-input"
        />
      </div>
      <div class="date-filters">
        <input
          v-model="dateFrom"
          type="date"
          class="date-input"
          placeholder="開始日"
          @change="fetchInterviews"
        />
        <span class="date-separator">〜</span>
        <input
          v-model="dateTo"
          type="date"
          class="date-input"
          placeholder="終了日"
          @change="fetchInterviews"
        />
      </div>
    </div>

    <!-- 面接リストテーブル -->
    <div class="table-container" v-if="!loading">
      <table class="interview-table">
        <thead>
          <tr>
            <th width="40">順序</th>
            <th width="80">強調表示</th>
            <th>応募者名</th>
            <th>面接開始日時</th>
            <th>ステージ</th>
            <th>ステータス</th>
            <th>コメント</th>
            <th width="100">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="interview in interviews"
            :key="interview.id"
            :class="{
              'highlighted-row': interview.is_highlighted || interview.is_auto_highlighted,
              'auto-highlighted': interview.is_auto_highlighted,
              'manual-highlighted': interview.is_highlighted && !interview.is_auto_highlighted
            }"
          >
            <td>
              <input
                v-model.number="interview.interview_sort_order"
                type="number"
                class="sort-order-input"
                @change="updateSortOrder(interview)"
                min="0"
                max="9999"
              />
            </td>
            <td>
              <div class="highlight-cell">
                <input
                  v-model="interview.is_highlighted"
                  type="checkbox"
                  class="highlight-checkbox"
                  @change="updateHighlight(interview)"
                />
                <span v-if="interview.is_auto_highlighted" class="auto-highlight-badge">
                  3日以内
                </span>
              </div>
            </td>
            <td>{{ interview.seeker_name }}</td>
            <td>{{ formatDateTime(interview.interview_start) }}</td>
            <td>{{ interview.stage || '-' }}</td>
            <td>{{ interview.status || '-' }}</td>
            <td>{{ interview.interview_comment || '-' }}</td>
            <td>
              <button
                @click="viewSeeker(interview.seeker_id)"
                class="btn-view"
              >
                詳細
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- ページネーション -->
      <div class="pagination" v-if="totalPages > 1">
        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="page-button"
        >
          前へ
        </button>
        <span class="page-info">
          {{ currentPage }} / {{ totalPages }}
        </span>
        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="page-button"
        >
          次へ
        </button>
      </div>
    </div>

    <!-- ローディング -->
    <div v-else class="loading-container">
      <i class="fas fa-spinner fa-spin"></i> 読み込み中...
    </div>
  </div>
</template>

<script>
import directtalkApi from '@/api/directtalk'

export default {
  name: 'InterviewList',
  data() {
    return {
      interviews: [],
      loading: false,
      searchQuery: '',
      dateFrom: '',
      dateTo: '',
      currentPage: 1,
      pageSize: 50,
      totalCount: 0,
      totalPages: 1,
      searchTimer: null
    }
  },
  computed: {
    selectedCompanyHash() {
      return this.$store.state.selectedCompanyHash
    }
  },
  created() {
    this.fetchInterviews()
  },
  methods: {
    // 面接一覧取得
    async fetchInterviews() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize
        }
        
        if (this.searchQuery) params.search = this.searchQuery
        if (this.dateFrom) params.date_from = this.dateFrom
        if (this.dateTo) params.date_to = this.dateTo

        // 修正: directtalkApiを使用
        const response = await directtalkApi.getInterviewList(params)
        
        if (response.data) {
          this.interviews = response.data.interviews || []
          this.totalCount = response.data.count || 0
          this.totalPages = response.data.total_pages || 1
          this.currentPage = response.data.page || 1
        }
      } catch (error) {
        console.error('面接一覧取得エラー:', error)
        this.$message.error('面接一覧の取得に失敗しました')
      } finally {
        this.loading = false
      }
    },

    // 検索のデバウンス
    debounceSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1
        this.fetchInterviews()
      }, 500)
    },

    // 並び順更新（入力検証追加）
    async updateSortOrder(interview) {
      // 入力検証
      if (interview.interview_sort_order < 0) {
        interview.interview_sort_order = 0
        this.$message.warning('並び順は0以上の値を入力してください')
      } else if (interview.interview_sort_order > 9999) {
        interview.interview_sort_order = 9999
        this.$message.warning('並び順は9999以下の値を入力してください')
      }
      
      try {
        // 修正: directtalkApiを使用
        await directtalkApi.updateInterview({
          id: interview.id,
          interview_sort_order: interview.interview_sort_order
        })
        this.$message.success('並び順を更新しました')
        // リストを再取得して並び順を反映
        await this.fetchInterviews()
      } catch (error) {
        console.error('並び順更新エラー:', error)
        this.$message.error('並び順の更新に失敗しました')
      }
    },

    // 強調表示更新（修正なし、APIメソッド名のみ変更）
    async updateHighlight(interview) {
      try {
        // 修正: directtalkApiを使用
        await directtalkApi.updateInterview({
          id: interview.id,
          is_highlighted: interview.is_highlighted
        })
        this.$message.success('強調表示を更新しました')
      } catch (error) {
        console.error('強調表示更新エラー:', error)
        this.$message.error('強調表示の更新に失敗しました')
        // エラー時は元に戻す
        interview.is_highlighted = !interview.is_highlighted
      }
    },

    // 日時フォーマット
    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      try {
        const date = new Date(dateStr)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}/${month}/${day} ${hours}:${minutes}`
      } catch (e) {
        return dateStr
      }
    },

    // 応募者詳細表示
    viewSeeker(seekerId) {
      this.$router.push({
        name: 'SeekerDetail',
        params: { id: seekerId }
      })
    },

    // リフレッシュ
    refreshList() {
      this.fetchInterviews()
    },

    // ページ変更
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.fetchInterviews()
      }
    }
  }
}
</script>

<style scoped>
.interview-list-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.interview-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.interview-list-header h2 {
  font-size: 24px;
  color: #333;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.btn-refresh {
  padding: 8px 16px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-refresh:hover {
  background-color: #357abd;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.filter-section {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-box {
  flex: 1;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.date-filters {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.date-separator {
  color: #666;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.interview-table {
  width: 100%;
  border-collapse: collapse;
}

.interview-table th,
.interview-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.interview-table th {
  background-color: #f8f8f8;
  font-weight: bold;
  color: #555;
}

.interview-table tbody tr:hover {
  background-color: #f9f9f9;
}

/* 既存のスタイルは維持 */

/* 追加スタイル */
.highlight-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 自動強調表示（前後3日以内） */
.auto-highlighted {
  background-color: #fff3cd !important;
}

/* 手動強調表示 */
.manual-highlighted {
  background-color: #d1ecf1 !important;
}

/* 両方該当する場合 */
.highlighted-row.auto-highlighted.manual-highlighted {
  background: linear-gradient(to right, #fff3cd 50%, #d1ecf1 50%) !important;
}

/* 自動強調表示バッジ */
.auto-highlight-badge {
  font-size: 10px;
  background-color: #ffc107;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
}

/* 並び順入力フィールドの調整 */
.sort-order-input {
  width: 60px;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
}

/* エラー状態のスタイル */
.sort-order-input:invalid {
  border-color: #dc3545;
  background-color: #f8d7da;
}

.highlight-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.btn-view {
  padding: 6px 12px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.btn-view:hover {
  background-color: #357abd;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 20px;
}

.page-button {
  padding: 8px 16px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.page-button:hover:not(:disabled) {
  background-color: #357abd;
}

.page-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.loading-container {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
}
</style>


// WEBPACK FOOTER //
// src/views/InterviewList/InterviewList.vue